<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="Reimbursement" label="Reimbursement" :active="true" />
    </breadcrumb-list>

    <!-- Past Due Notification Banner -->
    <div
      v-if="user && user.has_past_due_subscription && hasReimbursementFeature"
      class="notification is-danger is-light has-text-centered mb-0"
      style="border-radius: 0"
    >
      <span>
        Subscription past due. Please
        <a href="#" @click.prevent="handleUpdatePayment"><strong>Update Payment Method</strong></a>
      </span>
    </div>

    <!-- Show content blurred by default, remove blur only if user has access -->
    <section :class="{ 'content-blurred': shouldBlockAccess }">
      <div class="container is-max-hd">
        <div class="columns pt-2">
          <!-- Search Form -->
          <div class="column is-7">
            <div class="box">
              <div class="title-with-details mb-4">
                <h2 class="title is-4">Reimbursement Search</h2>
                <a @click="showReimbursementModal = true" class="feature-details-link-title">
                  <span class="details-text">See details</span>
                  <b-icon icon="magnify-plus" size="is-small" class="bounce-icon"></b-icon>
                </a>
              </div>
              <div class="columns is-mobile is-centered mb-3">
                <div class="column is-6">
                  <div
                    class="flow-card has-text-centered p-4 is-clickable"
                    :class="{ 'is-selected': selectedFlow === 'bcbs' }"
                    @click="selectFlow('bcbs')"
                  >
                    <span class="icon is-large flow-icon">
                      <b-icon icon="shield-check" size="is-large"></b-icon>
                    </span>
                    <p class="title is-5 mt-3 flow-title">BCBS</p>
                  </div>
                </div>
                <div class="column is-6">
                  <div
                    class="flow-card has-text-centered p-4 is-clickable"
                    :class="{ 'is-selected': selectedFlow === 'other' }"
                    @click="selectFlow('other')"
                  >
                    <span class="icon is-large flow-icon">
                      <b-icon icon="hospital-building" size="is-large"></b-icon>
                    </span>
                    <p class="title is-5 mt-3 flow-title">Other Payers</p>
                  </div>
                </div>
              </div>

              <div v-if="selectedFlow" class="mt-3">
                <div class="field" v-if="selectedFlow === 'bcbs'">
                  <label class="label">
                    Prefix
                    <span class="has-text-danger">*</span>
                  </label>
                  <div class="control">
                    <b-autocomplete
                      v-model="prefix"
                      :data="prefixSuggestions"
                      field="prefix"
                      placeholder="Enter prefix (min. 1 character)"
                      :loading="isLoading"
                      @typing="searchPrefix"
                      @select="onPrefixSelect"
                      :min-length="1"
                      :class="{ 'is-danger': isPrefixRequired }"
                    >
                      <template #empty>No results found</template>
                    </b-autocomplete>
                  </div>
                  <p class="help is-danger" v-if="isPrefixRequired">
                    Prefix is required for BCBS reimbursement search
                  </p>
                </div>

                <div class="field" v-if="selectedFlow === 'other'">
                  <label class="label">
                    Payer
                    <span class="has-text-danger">*</span>
                  </label>
                  <div class="control">
                    <b-autocomplete
                      v-model="payer"
                      :data="payerSuggestions"
                      field="name"
                      placeholder="Enter payer name (min. 2 characters)"
                      :loading="isLoading"
                      @typing="searchPayer"
                      @select="onPayerSelect"
                      :min-length="2"
                      :class="{ 'is-danger': isPayerRequired }"
                    >
                      <template #empty>No results found</template>
                    </b-autocomplete>
                  </div>
                  <p class="help is-danger" v-if="isPayerRequired">
                    Payer is required for reimbursement search
                  </p>
                </div>

                <div class="columns is-mobile group-employer-row">
                  <div class="column">
                    <div class="field">
                      <label class="label">Group Number</label>
                      <div class="control">
                        <b-autocomplete
                          v-model="group"
                          :data="groupSuggestions"
                          field="number"
                          placeholder="Group # (min. 2 chars)"
                          :loading="isLoading"
                          @typing="searchGroup"
                          @select="onGroupSelect"
                          :min-length="2"
                        >
                          <template #empty>No results found</template>
                        </b-autocomplete>
                      </div>
                    </div>
                  </div>
                  <div class="column">
                    <div class="field">
                      <label class="label">Employer</label>
                      <div class="control">
                        <b-autocomplete
                          v-model="employer"
                          :data="employerSuggestions"
                          field="name"
                          placeholder="Employer (min. 2 chars)"
                          :loading="isLoading"
                          @typing="searchEmployer"
                          @select="onEmployerSelect"
                          :min-length="2"
                        >
                          <template #empty>No results found</template>
                        </b-autocomplete>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Disclaimer Section -->
                <div class="disclaimer-section" v-if="selectedFlow">
                  <div class="disclaimer-container">
                    <p
                      class="help is-info disclaimer-message"
                      :class="{
                        'disclaimer-visible': showDisclaimer,
                        'disclaimer-hidden': !showDisclaimer,
                      }"
                    >
                      <b-icon icon="information-outline" size="is-small"></b-icon>
                      {{ disclaimerText }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Results Panel -->
          <div class="column is-5">
            <div class="box">
              <h2 class="title is-3 has-text-centered has-text-primary estimate-title">
                Reimbursement Estimate
              </h2>

              <!-- Local Allowable Rates Display -->
              <div v-if="isLocalAllowable" class="has-text-centered py-6">
                <div class="notification is-info is-light">
                  <p class="title is-4 has-text-centered mb-4">Local Allowable Rates</p>
                  <p>
                    This BCBS prefix uses local allowable rates. Reimbursement rates are determined
                    by your local Blue Cross Blue Shield plan and may vary by geographic region.
                  </p>
                </div>
              </div>

              <!-- Standard Aggregation Display -->
              <template v-else>
                <div class="level is-mobile mb-4 has-background-primary-light py-4 px-4 rounded-lg">
                  <div class="level-item has-text-centered">
                    <div>
                      <p class="heading has-text-primary">Average</p>
                      <p class="title has-text-primary">
                        {{ formatPercentage(aggregationResults?.avg) }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="columns is-mobile is-multiline">
                  <div class="column is-6">
                    <div class="has-text-centered">
                      <p class="heading">Detox</p>
                      <p class="title is-5">
                        {{ formatPercentage(aggregationResults?.dtx) }}
                      </p>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="has-text-centered">
                      <p class="heading">Residential</p>
                      <p class="title is-5">
                        {{ formatPercentage(aggregationResults?.rtc) }}
                      </p>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="has-text-centered">
                      <p class="heading">Partial Hosp.</p>
                      <p class="title is-5">
                        {{ formatPercentage(aggregationResults?.php) }}
                      </p>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="has-text-centered">
                      <p class="heading">Intensive OP</p>
                      <p class="title is-5">
                        {{ formatPercentage(aggregationResults?.iop) }}
                      </p>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="has-text-centered">
                      <p class="heading">Outpatient</p>
                      <p class="title is-5">
                        {{ formatPercentage(aggregationResults?.op) }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Important Note Section -->
                <div class="notification is-info is-light mt-4">
                  <p>
                    These estimates are based on historical data and represent percentage allowed on
                    charges. Actual reimbursement may vary based on specific plan details,
                    deductibles, co-insurance, and other factors at time of claim submission.
                  </p>
                </div>

                <div class="has-text-centered mt-4" v-if="aggregationResults?.match_level">
                  <p class="is-size-7 mt-2" v-if="matchMessage">
                    <strong>{{ matchMessage }}</strong>
                  </p>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Reimbursement Feature Detail Modal -->
    <b-modal v-model="showReimbursementModal" :width="640" scroll="keep">
      <ReimbursementFeatureDetail></ReimbursementFeatureDetail>
    </b-modal>

    <!-- Access Denied Modal with Full Screen Blur -->
    <b-modal
      v-model="showAccessModal"
      :width="640"
      scroll="keep"
      :can-cancel="false"
      :close-on-confirm="false"
      custom-class="access-denied-modal"
    >
      <div class="box access-modal-content">
        <div class="has-text-centered mb-5">
          <b-icon icon="lock-outline" size="is-large" type="is-danger"></b-icon>
          <h2 class="title is-4 mt-3">
            {{ reimbursementNoticeMessage }}
          </h2>
        </div>

        <div class="has-text-centered" v-if="showSubscriptionActions">
          <div class="modal-actions-compact">
            <b-button
              type="is-primary"
              size="is-medium"
              @click="
                user && user.has_past_due_subscription
                  ? handleUpdatePayment()
                  : goToSubscriptionPage()
              "
            >
              {{ user && user.has_past_due_subscription ? 'Update Payment' : 'Enable Feature' }}
            </b-button>
            <span class="action-separator">or</span>
            <a
              href="https://meetings.hubspot.com/bspircu/reimbursement"
              target="_blank"
              class="has-text-link is-size-6"
            >
              Talk to Sales
            </a>
          </div>
        </div>

        <div class="has-text-centered mt-4">
          <b-button type="is-light" size="is-medium" @click="goBack">
            Nevermind, take me back
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import request from '../request';
import BreadcrumbList from '../components/BreadcrumbList.vue';
import BreadcrumbLink from '../components/BreadcrumbLink.vue';
import ReimbursementFeatureDetail from '../components/ReimbursementFeatureDetail.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'Reimbursement',
  components: {
    BreadcrumbList,
    BreadcrumbLink,
    ReimbursementFeatureDetail,
  },
  beforeDestroy() {
    clearTimeout(this._autocompleteTimer);
  },
  created() {
    this.selectFlow('bcbs');

    // Check access based on user object
    // Don't show modal for past due subscriptions - they'll see the banner instead
    if (this.shouldBlockAccess) {
      this.showAccessModal = true;
    }
  },
  data() {
    return {
      selectedFlow: 'bcbs',
      prefix: '',
      payer: '',
      group: '',
      employer: '',
      prefixSuggestions: [],
      payerSuggestions: [],
      groupSuggestions: [],
      employerSuggestions: [],
      isLoading: false,
      aggregationResults: null,
      selectedPrefix: null,
      selectedPayer: null,
      selectedGroup: null,
      selectedEmployer: null,
      showReimbursementModal: false,
      subscriptionNoticeClosed: false,
      showAccessModal: false,
    };
  },
  computed: {
    ...mapGetters(['user']),
    canAggregate() {
      if (this.selectedFlow === 'bcbs') {
        // BCBS flow: Only needs prefix selected
        return this.selectedPrefix !== null;
      } else if (this.selectedFlow === 'other') {
        // Other payers flow: Needs payer AND (group OR employer)
        return (
          this.selectedPayer !== null &&
          (this.selectedGroup !== null || this.selectedEmployer !== null)
        );
      }
      return false;
    },
    matchMessage() {
      if (!this.aggregationResults) {
        return '';
      }
      let level = this.aggregationResults.match_level;
      // Blues and Non-Blues
      if (level === 2) {
        return 'No Group Number data was available. Showing all reimbursement data for the Employer.';
      }
      // Only Blues
      if (level === 1) {
        return 'No Employer data was available. Showing all reimbursement data for the Alpha Prefix.';
      }
      return '';
    },
    isLocalAllowable() {
      // Check if aggregation results indicate local allowable rates
      return this.aggregationResults?.status === 'local_allowable';
    },
    hasReimbursementFeature() {
      // Check if user has access via feature flag OR override flag
      if (!this.user) {
        return false;
      }

      // First check if organization has access
      const organizationHasAccess =
        this.user.has_feature_reimbursement || this.user.has_override_reimbursement;

      // If organization doesn't have access, user can't access
      if (!organizationHasAccess) {
        return false;
      }

      // Check user permission
      if (!this.user.perm_reimbursements) {
        return false;
      }

      // If has override access, allow immediately (trial/override access)
      if (this.user.has_override_reimbursement) {
        return true;
      }

      // For paid plans, allow if has active or past due subscription
      // (they paid for the feature, let them use it!)
      if (this.user.has_active_subscription || this.user.has_past_due_subscription) {
        return true;
      }

      return false;
    },
    hasOrganizationAccess() {
      if (!this.user) {
        return false;
      }
      return this.user.has_feature_reimbursement || this.user.has_override_reimbursement;
    },
    shouldBlockAccess() {
      // Block access only if:
      // 1. Organization doesn't have the feature, OR
      // 2. User doesn't have permission
      // Don't block for past due subscriptions if organization has the feature
      if (!this.user) {
        return true;
      }

      // If organization doesn't have access at all, block
      if (!this.hasOrganizationAccess) {
        return true;
      }

      // If organization has access but user doesn't have permission, block
      if (!this.user.perm_reimbursements) {
        return true;
      }

      // If past due but organization has the feature, don't block (show banner instead)
      if (this.user.has_past_due_subscription && this.hasOrganizationAccess) {
        return false;
      }

      // Otherwise use the feature check
      return !this.hasReimbursementFeature;
    },
    reimbursementNoticeMessage() {
      // Modal only shows when user doesn't have access
      if (!this.user) {
        return '';
      }

      // If past due subscription, show payment update message
      if (this.user.has_past_due_subscription) {
        return 'Subscription past due. Please update payment to continue';
      }

      // If organization doesn't have access, show subscription message
      if (!this.hasOrganizationAccess) {
        return "You're not subscribed to the Reimbursement feature";
      }

      // If organization has access but user doesn't have permission
      if (this.hasOrganizationAccess && !this.user.perm_reimbursements) {
        return 'To access this feature, have a manager on your account give you privileges from the user settings';
      }

      return null;
    },
    showSubscriptionActions() {
      // Show actions if organization doesn't have access
      // OR if user has past due subscription (needs to update payment or enable feature)
      return !this.hasOrganizationAccess || (this.user && this.user.has_past_due_subscription);
    },
    isPrefixRequired() {
      // Check if prefix is required but not selected in BCBS flow
      if (this.selectedFlow !== 'bcbs') {
        return false;
      }

      // Prefix is required if user has entered other fields but no prefix selected
      const hasOtherFields = this.selectedGroup !== null || this.selectedEmployer !== null;
      const noPrefixSelected = this.selectedPrefix === null;

      return hasOtherFields && noPrefixSelected;
    },
    isPayerRequired() {
      // Check if payer is required but not selected in other payers flow
      if (this.selectedFlow !== 'other') {
        return false;
      }

      // Payer is required if user has entered other fields but no payer selected
      const hasOtherFields = this.selectedGroup !== null || this.selectedEmployer !== null;
      const noPayerSelected = this.selectedPayer === null;

      return hasOtherFields && noPayerSelected;
    },
    showDisclaimer() {
      // Show disclaimer when either payer guidance or accuracy disclaimer should appear
      return this.shouldShowPayerGuidance || this.shouldShowAccuracyDisclaimer;
    },
    disclaimerText() {
      // Return appropriate text based on current state
      if (this.shouldShowPayerGuidance) {
        return 'Now select a Group Number or Employer to get reimbursement data';
      } else if (this.shouldShowAccuracyDisclaimer) {
        return 'For most accurate reimbursement estimations, all 3 fields must be selected, estimates based on less information may not be as accurate';
      }
      return '';
    },
    shouldShowPayerGuidance() {
      // Show helpful guidance in other flow after payer is selected but before group/employer
      if (this.selectedFlow !== 'other') {
        return false;
      }

      // Show guidance if payer is selected but no group/employer yet
      const payerSelected = this.selectedPayer !== null;
      const noAdditionalFields = this.selectedGroup === null && this.selectedEmployer === null;

      return payerSelected && noAdditionalFields;
    },
    shouldShowAccuracyDisclaimer() {
      // Show accuracy disclaimer when fields are selected but not all 3
      if (this.selectedFlow === 'bcbs') {
        // BCBS flow: Show if prefix selected but not all 3 fields
        const prefixSelected = this.selectedPrefix !== null;
        const allThreeFields =
          this.selectedPrefix !== null &&
          this.selectedGroup !== null &&
          this.selectedEmployer !== null;
        return prefixSelected && !allThreeFields;
      } else if (this.selectedFlow === 'other') {
        // Other flow: Show if payer + 1 other field selected but not all 3
        const payerSelected = this.selectedPayer !== null;
        const hasOneAdditionalField = this.selectedGroup !== null || this.selectedEmployer !== null;
        const allThreeFields =
          this.selectedPayer !== null &&
          this.selectedGroup !== null &&
          this.selectedEmployer !== null;
        return payerSelected && hasOneAdditionalField && !allThreeFields;
      }
      return false;
    },
  },
  methods: {
    selectFlow(flow) {
      this.selectedFlow = flow;
      this.resetForm();
    },
    resetForm() {
      this.prefix = '';
      this.payer = '';
      this.group = '';
      this.employer = '';
      this.selectedPrefix = null;
      this.selectedPayer = null;
      this.selectedGroup = null;
      this.selectedEmployer = null;
    },
    searchPrefix(term) {
      if (term.length < 1) return;
      this.isLoading = true;
      clearTimeout(this._autocompleteTimer);
      this._autocompleteTimer = setTimeout(async () => {
        try {
          const response = await request.get('/reimbursements/prefixes', { params: { term } });
          this.prefixSuggestions = response.data.reimbursement_prefixes;
        } catch (error) {
          console.error('Error searching prefixes:', error);
          this.$buefy.toast.open({ message: 'Failed to search prefixes', type: 'is-danger' });
        } finally {
          this.isLoading = false;
        }
      }, 400);
    },
    searchPayer(term) {
      if (term.length < 2) return;
      this.isLoading = true;
      clearTimeout(this._autocompleteTimer);
      this._autocompleteTimer = setTimeout(async () => {
        try {
          const response = await request.get('/reimbursements/payers', { params: { term } });
          this.payerSuggestions = response.data.reimbursement_payers;
        } catch (error) {
          console.error('Error searching payers:', error);
          this.$buefy.toast.open({ message: 'Failed to search payers', type: 'is-danger' });
        } finally {
          this.isLoading = false;
        }
      }, 400);
    },
    searchGroup(term) {
      if (term.length < 2) return;
      this.isLoading = true;
      clearTimeout(this._autocompleteTimer);
      this._autocompleteTimer = setTimeout(async () => {
        try {
          const response = await request.get('/reimbursements/groups', { params: { term } });
          this.groupSuggestions = response.data.reimbursement_groups;
        } catch (error) {
          console.error('Error searching groups:', error);
          this.$buefy.toast.open({ message: 'Failed to search groups', type: 'is-danger' });
        } finally {
          this.isLoading = false;
        }
      }, 400);
    },
    searchEmployer(term) {
      if (term.length < 2) return;
      this.isLoading = true;
      clearTimeout(this._autocompleteTimer);
      this._autocompleteTimer = setTimeout(async () => {
        try {
          const response = await request.get('/reimbursements/employers', { params: { term } });
          this.employerSuggestions = response.data.reimbursement_employers;
        } catch (error) {
          console.error('Error searching employers:', error);
          this.$buefy.toast.open({ message: 'Failed to search employers', type: 'is-danger' });
        } finally {
          this.isLoading = false;
        }
      }, 400);
    },
    onPrefixSelect(item) {
      if (item) {
        this.selectedPrefix = item;
        this.prefix = item.prefix;
        this.aggregate();
      } else {
        this.selectedPrefix = null;
        this.prefix = '';
        if (this.canAggregate) {
          this.aggregate();
        } else {
          this.aggregationResults = null;
        }
      }
    },
    onPayerSelect(item) {
      if (item) {
        this.selectedPayer = item;
        this.payer = item.name;
        if (this.canAggregate) {
          this.aggregate();
        }
      } else {
        this.selectedPayer = null;
        this.payer = '';
        // Use canAggregate to determine next action
        if (this.canAggregate) {
          this.aggregate();
        } else {
          this.aggregationResults = null;
        }
      }
    },
    onGroupSelect(item) {
      if (item) {
        this.selectedGroup = item;
        this.group = item.number;
        if (this.canAggregate) {
          this.aggregate();
        }
      } else {
        this.selectedGroup = null;
        this.group = '';
        // Use canAggregate to determine next action
        if (this.canAggregate) {
          this.aggregate();
        } else {
          this.aggregationResults = null;
        }
      }
    },
    onEmployerSelect(item) {
      if (item) {
        this.selectedEmployer = item;
        this.employer = item.name;
        if (this.canAggregate) {
          this.aggregate();
        }
      } else {
        this.selectedEmployer = null;
        this.employer = '';
        if (this.canAggregate) {
          this.aggregate();
        } else {
          this.aggregationResults = null;
        }
      }
    },
    formatPercentage(value) {
      if (!value && value !== 0) return '-';
      return `${value.toFixed(2)}%`;
    },
    async aggregate() {
      this.isLoading = true;
      try {
        const filter = {
          prefix_id: this.selectedFlow === 'bcbs' ? this.selectedPrefix?.id : null,
          payer_id: this.selectedFlow === 'other' ? this.selectedPayer?.id : null,
          group_id: this.selectedGroup?.id,
          employer_id: this.selectedEmployer?.id,
        };

        const response = await request.post('/reimbursements/aggregate', filter);
        this.aggregationResults = response.data;
      } catch (error) {
        console.error('Error aggregating reimbursements:', error);
        this.$buefy.toast.open({
          message: 'Failed to analyze reimbursements',
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    handleUpdatePayment() {
      this.isLoading = true;
      request.post('/subscriptions/portal-session').then((resp) => {
        window.location.href = resp.data.redirect_url;
      });
    },
    goToSubscriptionPage() {
      // Navigate to subscription/billing page with enable feature parameter
      this.$router
        .push({
          path: '/organization/settings/plan',
        })
        .then(() => {
          // Wait for the page to load, then scroll to the reimbursement section
          this.$nextTick(() => {
            setTimeout(() => {
              // Try to find the reimbursement section and scroll to it
              const reimbursementElement = document.querySelector(
                '[data-feature="reimbursement"], [data-toggle="reimbursement"], .reimbursement-toggle, #reimbursement-feature'
              );
              if (reimbursementElement) {
                reimbursementElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              } else {
                // If specific element not found, scroll down to show toggles area
                window.scrollTo({
                  top: window.innerHeight,
                  behavior: 'smooth',
                });
              }
            }, 500); // Small delay to ensure page is fully loaded
          });
        })
        .catch(() => {
          // Fallback: use full URL if routing fails
          window.location.href = '/organization/settings/plan?enableFeature=reimbursement';
        });
    },
    closeAccessModal() {
      this.showAccessModal = false;
    },
    closeSubscriptionNotice() {
      this.subscriptionNoticeClosed = true;
    },
    goBack() {
      // Go back to the previous page
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.heading {
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #7a7a7a;
}
.title {
  margin-bottom: 0 !important;
}
.is-clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}
.is-clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}
.icon.is-large i {
  font-size: 2rem;
}
.flow-card {
  background: white;
  border: 2px solid #f5f5f5;
  border-radius: 8px;
  transition: all 0.3s ease;
}
.flow-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border-color: #dbdbdb;
}
.flow-card.is-selected {
  background: #f0f6ff;
  border-color: #7957d5;
  box-shadow: 0 4px 15px rgba(121, 87, 213, 0.1);
}
.rounded-lg {
  border-radius: 8px;
}
.estimate-title {
  padding-bottom: 1.5rem;
  margin-bottom: 2rem !important;
  border-bottom: 2px solid #7957d5;
  position: relative;
}
.estimate-title::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 25%;
  right: 25%;
  height: 2px;
  background-color: #7957d5;
}

.title-with-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

@media (max-width: 1023px) {
  .title-with-details {
    display: none;
  }
}

/* Mobile adjustments for compact layout */
@media (max-width: 768px) {
  .flow-card {
    padding: 0.5rem !important;
  }

  .flow-card .flow-icon {
    transform: scale(0.7);
  }

  .flow-card .flow-title {
    font-size: 0.85rem !important;
    margin-top: 0.25rem !important;
  }

  /* Reduce box padding */
  .box {
    padding: 0.75rem !important;
  }

  /* Compact field spacing */
  .field {
    margin-bottom: 0.5rem !important;
  }

  .label {
    font-size: 0.8rem !important;
    margin-bottom: 0.25rem !important;
  }

  /* Group/Employer row spacing */
  .group-employer-row {
    margin-bottom: 0 !important;
  }

  .group-employer-row .column {
    padding: 0.25rem !important;
  }

  /* Compact disclaimer */
  .disclaimer-section {
    margin-top: 0.5rem !important;
    padding-top: 0.25rem !important;
  }

  .disclaimer-container {
    height: auto !important;
    min-height: 30px;
  }

  .disclaimer-message {
    font-size: 0.75rem !important;
  }

  /* Compact results panel */
  .estimate-title {
    font-size: 1.25rem !important;
    padding-bottom: 0.75rem !important;
    margin-bottom: 1rem !important;
  }

  .level.is-mobile {
    padding: 0.5rem !important;
    margin-bottom: 0.5rem !important;
  }

  .level-item .heading {
    font-size: 0.7rem !important;
  }

  .level-item .title {
    font-size: 1.25rem !important;
  }

  /* Results grid compact */
  .columns.is-multiline .column {
    padding: 0.25rem !important;
  }

  .columns.is-multiline .heading {
    font-size: 0.65rem !important;
  }

  .columns.is-multiline .title.is-5 {
    font-size: 0.9rem !important;
  }

  /* Compact notification */
  .notification.is-info {
    padding: 0.5rem 0.75rem !important;
    font-size: 0.75rem !important;
    margin-top: 0.5rem !important;
  }

  /* Reduce section padding */
  .pt-2 {
    padding-top: 0.25rem !important;
  }

  .mb-3 {
    margin-bottom: 0.5rem !important;
  }

  .mb-4 {
    margin-bottom: 0.75rem !important;
  }

  .mt-3 {
    margin-top: 0.5rem !important;
  }
}

.feature-details-link-title {
  color: #7c3aed;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;
  font-size: 0.875rem;
  text-decoration: none;
}

.feature-details-link-title:hover {
  color: #6d28d9;
  text-decoration: underline;
}

.details-text {
  font-weight: 500;
}

.bounce-icon {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-5px);
  }
  60% {
    transform: translateY(-3px);
  }
}

.subscription-notice {
  background-color: #fef7f7;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.subscription-notice p {
  margin-bottom: 0.5rem;
}

.subscription-notice p:last-child {
  margin-bottom: 0;
}

.subscription-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.mx-1 {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
}

.notice-close-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
}
</style>

<style>
/* Global styles for the access denied modal */
.access-denied-modal .modal-background {
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.7) !important;
}

.access-denied-modal .modal-card,
.access-denied-modal .modal-content {
  position: relative;
  z-index: 1000;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.access-modal-content {
  border-radius: 12px;
  border: 2px solid #e53e3e;
  background: white;
}

/* Ensure the modal cannot be closed by clicking outside */
.access-denied-modal .modal-background {
  pointer-events: none;
}

.access-denied-modal .modal-content {
  pointer-events: auto;
}

.modal-actions-compact {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.action-separator {
  font-size: 0.875rem;
  color: #7a7a7a;
  margin: 0 0.5rem;
}

.content-blurred {
  filter: blur(5px);
  pointer-events: none;
  user-select: none;
  opacity: 0.7;
}

/* Important Note Section styling - matches ReimbursementFeatureDetail */
.notification {
  border-left: 4px solid #3273dc;
}

/* Disclaimer section styling */
.disclaimer-section {
  margin-top: 1rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e5e5;
}

.disclaimer-container {
  height: 40px; /* Reserve space for multi-line messages to prevent layout shift */
  overflow: hidden;
  transition: all 0.3s ease;
}

.disclaimer-message {
  margin-bottom: 0 !important;
}

.disclaimer-visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.disclaimer-hidden {
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

/* Hide breadcrumbs on mobile for this page */
@media (max-width: 1023px) {
  .breadcrumb-section {
    display: none !important;
  }
}
</style>
